import { type Block, blocksToTiptap, toSafeHref } from "@klarity/schema";
import type { Editor } from "@tiptap/core";
import { getHost } from "./host";

// One link rule for the whole platform (plan 01 final review I2): toSafeHref from @klarity/schema
// accepts http(s) and mailto, lowercases the scheme and percent-encodes characters such as ( ) [ ].
export function validateHref(value: string): string | null {
  const v = value.trim();
  return v === "" || toSafeHref(v) !== null ? null : "Use a link that starts with https://, http:// or mailto:.";
}

export async function editLink(editor: Editor): Promise<void> {
  const current = editor.getAttributes("link").href as string | undefined;
  const value = await getHost(editor).askText({
    title: current ? "Edit link" : "Add link",
    label: "Link address",
    initial: current ?? "https://",
    confirmLabel: "Save link",
    validate: validateHref,
  });
  if (value === null || editor.isDestroyed || !editor.isEditable) return;
  const trimmed = value.trim();
  const href = trimmed === "" ? "" : (toSafeHref(trimmed) ?? "");
  if (href === "") {
    editor.chain().focus().extendMarkRange("link").unsetMark("link").run();
    return;
  }
  if (editor.state.selection.empty && !current) {
    editor
      .chain()
      .focus()
      .insertContent({ type: "text", text: href, marks: [{ type: "link", attrs: { href } }] })
      .run();
    return;
  }
  editor.chain().focus().extendMarkRange("link").setMark("link", { href }).run();
}

export async function insertPlaceholder(editor: Editor): Promise<void> {
  const label = await getHost(editor).askText({
    title: "Insert placeholder",
    label: "Label",
    confirmLabel: "Insert",
    validate: (v) => (/^[^[\]]+$/.test(v.trim()) ? null : "Use a short label without [ or ]."),
  });
  if (!label || editor.isDestroyed || !editor.isEditable) return;
  editor
    .chain()
    .focus()
    .insertContent({ type: "placeholder", attrs: { label: label.trim() } })
    .run();
}

/** Inserts block-format blocks at the cursor; an empty paragraph under the cursor is replaced. */
export function insertBlocks(editor: Editor, blocks: Block[]): boolean {
  return editor.chain().focus().insertContent(blocksToTiptap(blocks).content).run();
}
